import React, { useState, useRef, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { TextField, Paper, Box, Typography, IconButton, InputAdornment, CircularProgress } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
  },
  dropdown: {
    position: 'absolute',
    zIndex: 1300,
    marginTop: 4,
    width: '100%',
    maxHeight: 240,
    overflowY: 'auto',
    boxShadow: theme.shadows[8],
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    cursor: 'pointer',
    fontSize: '0.875rem',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    borderBottom: '1px solid ' + theme.palette.divider,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  itemHighlighted: {
    backgroundColor: 'rgba(29,66,138,0.12)',
    color: '#1d428a',
  },
  itemName: {
    fontWeight: 500,
  },
  itemEmail: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 200,
  },
  noMatch: {
    padding: theme.spacing(1.5, 2),
  },
}));

const ProspectTypeahead = ({ value, onChange, onSearch, placeholder = 'Search or type prospect name...', prospects = [], disabled = false, required = false }) => {
  const classes = useStyles();
  const [query, setQuery] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const prospectList = useMemo(() => {
    return prospects
      .map((p, idx) => ({
        id: p.officialUID || p.OfficialUID || idx,
        name: `${(p.firstName || p.FirstName || '').trim()} ${(p.lastName || p.LastName || '').trim()}`.trim(),
        email: (p.email || p.Email || '').trim(),
      }))
      .filter((p) => p.name.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [prospects]);

  const filtered = useMemo(() => {
    if (!query.trim() || query.trim().length < 3) return [];
    return prospectList.slice(0, 20);
  }, [query, prospectList]);

  useEffect(() => {
    if (value !== query) {
      setQuery(value || '');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll('[data-item]');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    setIsOpen(true);
    setHighlightedIndex(-1);

    if (onSearch) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (val.trim().length >= 3) {
        setIsSearching(true);
        debounceRef.current = setTimeout(() => {
          Promise.resolve(onSearch(val)).finally(() => {
            setIsSearching(false);
          });
        }, 300);
      } else {
        setIsSearching(false);
        onSearch(val);
      }
    }
  };

  const handleSelect = (prospect) => {
    setQuery(prospect.name);
    onChange(prospect.name, prospect);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    setQuery('');
    onChange('', null);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen || filtered.length === 0) {
      if (e.key === 'ArrowDown' && query.trim() && filtered.length > 0) {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
          handleSelect(filtered[highlightedIndex]);
        } else {
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (query.trim() && filtered.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={classes.container}>
      <TextField
        inputRef={inputRef}
        variant="outlined"
        size="small"
        fullWidth
        required={required}
        label="Prospect Name"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: query && !disabled ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {isOpen && query.trim().length >= 3 && isSearching && (
        <Paper className={`${classes.dropdown} ${classes.noMatch}`}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="textSecondary">
              Searching...
            </Typography>
          </Box>
        </Paper>
      )}

      {isOpen && !isSearching && filtered.length > 0 && (
        <Paper ref={dropdownRef} className={classes.dropdown}>
          {filtered.map((prospect, index) => (
            <Box
              key={prospect.id}
              data-item
              onClick={() => handleSelect(prospect)}
              className={`${classes.item} ${index === highlightedIndex ? classes.itemHighlighted : ''}`}
            >
              <span className={classes.itemName}>{prospect.name}</span>
              <span className={classes.itemEmail}>{prospect.email}</span>
            </Box>
          ))}
        </Paper>
      )}

      {isOpen && query.trim().length > 0 && query.trim().length < 3 && (
        <Paper className={`${classes.dropdown} ${classes.noMatch}`}>
          <Typography variant="caption" color="textSecondary">
            Please enter at least 3 characters to search.
          </Typography>
        </Paper>
      )}

      {isOpen && !isSearching && query.trim().length >= 3 && filtered.length === 0 && (
        <Paper className={`${classes.dropdown} ${classes.noMatch}`}>
          <Typography variant="caption" color="textSecondary">
            No matching prospects found. <span style={{ color: '#1d428a' }}>You can type a new name.</span>
          </Typography>
        </Paper>
      )}
    </div>
  );
};

export default ProspectTypeahead;
